import React, { type FunctionComponent } from 'react'
import styled from '@emotion/styled'
import { useDashMessage } from 'intl/intl.hooks'
import { type WarehouseEarlyPickingHour } from 'domains/shifts/components/utils/fetchEarlyHoursForWarehouse'
import { ShiftType, type ShopperWithShifts } from 'domains/shifts/components/types'
import { computeDailyMetrics } from 'domains/shifts/components/components/performance-overview/utils/computeDailyMetrics'
import { computeWeeklyMetrics } from 'domains/shifts/components/components/performance-overview/utils/computeWeeklyMetrics'
import { type StaffingLevel } from 'domains/shifts/components/components/staffing-levels/utils/types'
import {
  PickingPerformanceOverview,
  AggregateStorePerformanceMetrics,
} from 'gin-and-tonic/containers/picking-performance-overview/'
import { type StaffingDataForWeek } from 'domains/shifts/components/components/weekly-staffing/utils/types'
import { StaffingAlertRow } from 'domains/shifts/components/components/staffing-alert/StaffingAlertRow'

const PickingPerformanceContainer = styled.div({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
})

const PickingPerformanceRow = styled.div({
  display: 'flex',
  flexDirection: 'row',
})

interface PerformanceOverviewProps {
  shiftType: ShiftType
  staffingLevelsForDay: StaffingLevel[]
  shoppersForDay: ShopperWithShifts[]
  isWeeklyView: boolean
  staffingDataForWeek: StaffingDataForWeek
  warehouseEarlyHours: WarehouseEarlyPickingHour
  isLoading: boolean
  showAnalyticsCta?: boolean
  warehouseLocationId?: number
  retailerLocationId?: string
  useDevicePrioritization: boolean
  isRunnerDevicePrioritizationEnabled: boolean
  zoneOffset: number
}

export const PerformanceOverview: FunctionComponent<PerformanceOverviewProps> = ({
  shiftType,
  staffingLevelsForDay,
  shoppersForDay,
  isWeeklyView,
  staffingDataForWeek,
  warehouseEarlyHours,
  isLoading,
  showAnalyticsCta,
  warehouseLocationId,
  retailerLocationId,
  useDevicePrioritization,
  isRunnerDevicePrioritizationEnabled,
  zoneOffset,
}) => {
  const SHIFT_TYPE_HEADER = {
    [ShiftType.Picking]: useDashMessage('shiftsDomain.picking.performance'),
    [ShiftType.Running]: useDashMessage('shiftsDomain.running.performance'),
    [ShiftType.FullService]: useDashMessage('shiftsDomain.fullService.performance'),
    [ShiftType.Driving]: useDashMessage('shiftsDomain.driving.performance'),
  }

  const headerMessage = SHIFT_TYPE_HEADER[shiftType]

  const aggregateStoreMetrics = isWeeklyView
    ? computeWeeklyMetrics({
        shiftType,
        staffingDataForWeek,
        warehouseEarlyHours,
        useDevicePrioritization,
        shouldCalculateMatchRate: true,
        isRunnerDevicePrioritizationEnabled,
      })
    : computeDailyMetrics(shiftType, staffingLevelsForDay, shoppersForDay, true)

  if (shiftType !== ShiftType.Picking) {
    aggregateStoreMetrics.matchRate = null
  }

  return (
    <PickingPerformanceOverview
      title={headerMessage}
      isLoading={isLoading}
      showAnalyticsCta={showAnalyticsCta}
      warehouseLocationId={warehouseLocationId}
      retailerLocationId={retailerLocationId}
    >
      <PickingPerformanceContainer>
        <PickingPerformanceRow>
          <AggregateStorePerformanceMetrics {...aggregateStoreMetrics} />
        </PickingPerformanceRow>
        {!isWeeklyView && <StaffingAlertRow zoneOffset={zoneOffset} />}
      </PickingPerformanceContainer>
    </PickingPerformanceOverview>
  )
}
